import React, { useEffect, useState } from "react";
import {
  Box,
  Typography,
  Tabs,
  Tab,
  CircularProgress,
  Card,
  CardContent,
  useTheme,
  useMediaQuery,
  TextField,
  InputAdornment,
  MenuItem,
  Pagination,
  Button,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Stack
} from "@mui/material";
import { useParams } from "react-router-dom";
import { fetchCustomerOverview } from "../api/adminCustomerApi";
import { LineChart, Line, XAxis, YAxis, Tooltip, ResponsiveContainer } from "recharts";
import { format } from "date-fns";
import Grid from '@mui/material/GridLegacy';

const statusOptions = ["all", "pending", "paid", "partially_paid"];

const CustomerOverviewPage: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

  const [activeTab, setActiveTab] = useState(0);
  const [loading, setLoading] = useState(true);
  const [overviewData, setOverviewData] = useState<any>(null);
  const [page, setPage] = useState(1);
  const [statusFilter, setStatusFilter] = useState("all");
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");

const fetchData = async () => {
    setLoading(true);
    try {
      const params: any = { page, limit: 10 };
      if (statusFilter !== "all") params.status = statusFilter; // Only send if not 'all'
      if (startDate && endDate) {
        params.startDate = startDate;
        params.endDate = endDate;
      }
      const data = await fetchCustomerOverview(Number(id), params);
      setOverviewData(data);
    } catch (error) {
      console.error("Error fetching customer overview:", error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, [id, page, statusFilter, startDate, endDate]);

  const handleTabChange = (_: any, newValue: number) => setActiveTab(newValue);

  if (loading || !overviewData) {
    return <Box display="flex" justifyContent="center" mt={6}><CircularProgress /></Box>;
  }

  const { customer, stats, orders, trends } = overviewData;

  return (
    <Box sx={{ p: isMobile ? 2 : 4 }}>
      <Typography variant="h5" mb={2}>{customer.name} ({customer.email})</Typography>

      <Tabs value={activeTab} onChange={handleTabChange} sx={{ mb: 3 }}>
        <Tab label="Overview" />
        <Tab label="Orders" />
      </Tabs>

      {activeTab === 0 && (
        <Grid container spacing={3}>
          <Grid item xs={12} md={4}>
            <Card><CardContent><Typography variant="subtitle2">Total Orders</Typography><Typography variant="h6">{stats.totalOrders}</Typography></CardContent></Card>
          </Grid>
          <Grid item xs={12} md={4}>
            <Card><CardContent><Typography variant="subtitle2">Total Spent</Typography><Typography variant="h6">${stats.totalSpent.toFixed(2)}</Typography></CardContent></Card>
          </Grid>
          <Grid item xs={12} md={4}>
            <Card><CardContent><Typography variant="subtitle2">Outstanding Balance</Typography><Typography variant="h6" color="error">${stats.outstandingBalance.toFixed(2)}</Typography></CardContent></Card>
          </Grid>
          <Grid item xs={12} md={6}>
            <Card>
              <CardContent>
                <Typography variant="subtitle2">Spending Trends</Typography>
                <ResponsiveContainer width="100%" height={200}>
                  <LineChart data={trends.monthlySpending}>
                    <XAxis dataKey="month" />
                    <YAxis />
                    <Tooltip />
                    <Line type="monotone" dataKey="total" stroke={theme.palette.primary.main} strokeWidth={2} />
                  </LineChart>
                </ResponsiveContainer>
              </CardContent>
            </Card>
          </Grid>
        </Grid>
      )}

      {activeTab === 1 && (
        <Box>
          <Box sx={{ display: "flex", gap: 2, mb: 2, flexWrap: "wrap" }}>
            <TextField
              select
              label="Status"
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
              size="small"
              sx={{ minWidth: 120 }}
            >
              {statusOptions.map((opt) => <MenuItem key={opt} value={opt}>{opt}</MenuItem>)}
            </TextField>
            <TextField
              label="Start Date"
              type="date"
              value={startDate}
              onChange={(e) => setStartDate(e.target.value)}
              size="small"
              InputLabelProps={{ shrink: true }}
            />
            <TextField
              label="End Date"
              type="date"
              value={endDate}
              onChange={(e) => setEndDate(e.target.value)}
              size="small"
              InputLabelProps={{ shrink: true }}
            />
            <Button variant="outlined" onClick={() => setPage(1)}>Apply Filters</Button>
          </Box>

          {isMobile ? (
            <Stack spacing={2}>
              {orders.data.map((order: any) => (
                <Card key={order.id}>
                  <CardContent>
                    <Stack spacing={1}>
                      <Button
                        href={`/dashboard/orders/${order.id}`}
                        size="small"
                        variant="outlined"
                        sx={{ width: 'fit-content' }}
                      >
                        #{order.id}
                      </Button>
                      <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                        <Typography variant="body2">Total:</Typography>
                        <Typography variant="body2">${Number(order.total).toFixed(2)}</Typography>
                      </Box>
                      <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                        <Typography variant="body2">Status:</Typography>
                        <Typography variant="body2">{order.status}</Typography>
                      </Box>
                      <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                        <Typography variant="body2">Outstanding:</Typography>
                        <Typography variant="body2">${Number(order.outstanding_balance).toFixed(2)}</Typography>
                      </Box>
                      <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                        <Typography variant="body2">Date:</Typography>
                        <Typography variant="body2">
                          {format(new Date(order.created_at), "yyyy-MM-dd")}
                        </Typography>
                      </Box>
                    </Stack>
                  </CardContent>
                </Card>
              ))}
            </Stack>
          ) : (
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>ID</TableCell>
                  <TableCell>Total</TableCell>
                  <TableCell>Status</TableCell>
                  <TableCell>Outstanding</TableCell>
                  <TableCell>Date</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {orders.data.map((order: any) => (
                  <TableRow key={order.id}>
                    <TableCell>
                      <Button
                        href={`/dashboard/orders/${order.id}`}
                        size="small"
                        variant="outlined"
                      >
                        #{order.id}
                      </Button>
                    </TableCell>
                    <TableCell>${Number(order.total).toFixed(2)}</TableCell>
                    <TableCell>{order.status}</TableCell>
                    <TableCell>${Number(order.outstanding_balance).toFixed(2)}</TableCell>
                    <TableCell>{format(new Date(order.created_at), "yyyy-MM-dd")}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}

          <Box mt={2} display="flex" justifyContent="center">
            <Pagination
              count={orders.pagination.totalPages}
              page={page}
              onChange={(_, value) => setPage(value)}
              color="primary"
              size={isMobile ? "small" : "medium"}
            />
          </Box>
        </Box>
      )}
    </Box>
  );
};

export default CustomerOverviewPage;